import {
  EventType,
  HarmReport,
  InterventionDecision,
  ReviewStatus,
} from '@inplay/models';

export class HarmReportModel {
  private readonly data: HarmReport;

  constructor(data: HarmReport) {
    this.data = data;
  }

  get raw(): HarmReport {
    return this.data;
  }

  get originalReportId(): string | undefined {
    return this.data.originalReportId;
  }

  get reportRefNo(): string {
    return this.data.reportRefNo;
  }

  get playerId(): string {
    return this.data.playerProfile?.id ?? '';
  }

  get createdAt(): Date {
    return new Date(this.data.createdAt);
  }

  get expiresAt(): Date | undefined {
    if (!this.data.expiresAt) return undefined;
    return new Date(this.data.expiresAt);
  }

  get harmInstanceCountForPlayer(): string {
    return this.data.harmInstanceCountForPlayer;
  }

  get isFirstInstance(): boolean {
    return this.data.harmInstanceCountForPlayer === '1';
  }

  get isReviewed(): boolean {
    return this.data.reviewStatus === ReviewStatus.COMPLETE;
  }

  get isInterventionRequired(): boolean {
    return this.data.intervention?.decision === InterventionDecision.REQUIRED;
  }

  get reviewStatus(): ReviewStatus {
    return this.data.reviewStatus;
  }

  get interventionDecision(): InterventionDecision | undefined {
    return this.data.intervention?.decision;
  }

  get interventionStatus(): HarmReport['intervention']['status'] {
    return this.data.intervention?.status;
  }

  get deadlineType(): EventType.HARM | EventType.INTERVENTION | undefined {
    if (!this.expiresAt) return undefined;
    if (this.isFirstInstance) return EventType.INTERVENTION;
    if (!this.isReviewed) return EventType.HARM;
    if (this.isReviewed && this.isInterventionRequired)
      return EventType.INTERVENTION;
    return undefined;
  }

  get hasActiveDeadline(): boolean {
    return Boolean(this.expiresAt && this.deadlineType);
  }

  get createdByFullName(): string {
    if (!this.data.createdBy) return '';
    return `${this.data.createdBy.firstName} ${this.data.createdBy.lastName}`.trim();
  }

  get reviewedByFullName(): string {
    if (!this.data.reviewedBy) return '';
    return `${this.data.reviewedBy.firstName} ${this.data.reviewedBy.lastName}`.trim();
  }

  get playerProfile(): HarmReport['playerProfile'] {
    return this.data.playerProfile;
  }

  get playerDetails(): HarmReport['playerDetails'] {
    return this.data.playerDetails;
  }

  get signsOfGamblingHarm(): HarmReport['signsOfGamblingHarm'] {
    return this.data.signsOfGamblingHarm;
  }

  get venueBranch(): HarmReport['venueBranch'] {
    return this.data.venueBranch;
  }
}
